import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import posthog from "posthog-js";
import { z } from "zod";
import { safeCapture } from "@/lib/analytics/safeCapture";
import { META_PIXEL_EVENT, trackMetaEvent } from "@/lib/analytics/metaPixel";
import { markQuizStarted } from "@/lib/analytics/quizStartTracking";
import { humanizeApiMessage, normalizeDrfError } from "@/lib/api/errors";
import { clientFetch } from "@/lib/api/client";
import { proxyPath, type StepKey } from "@/lib/api/paths";
import { fetchGeographyOptions, type GeographyOptions } from "@/lib/api/recommendations/geography";
import {
  fetchPriorityDescriptions,
  type PriorityDescriptions,
} from "@/lib/api/recommendations/priorityDescriptions";
import { STEP_IMAGES, type QuizStepKey, type StepImageEntry } from "./QuizStepImage";
import { shuffleImagePool } from "./shuffleImagePool";
import { readLastShownSrc, writeLastShownSrc } from "./stepImageHistory";
import {
  prioritiesSchema,
  priorityField,
  missingConstraintFields,
} from "./quizSchemas";
import type { TripProfileDraft } from "./quizSchemas";
import type { PrioritiesDraft } from "./QuizStepPriorities";
import type { ConstraintsDraft } from "./QuizStepConstraints";
import {
  CURRENCY_CONFIG,
  DEFAULT_BUDGET_FLEXIBILITY,
  resolveInitialCurrency,
  isBudgetFlexibility,
  isSupportedCurrency,
  SHOW_PREFERRED_DESTINATION,
  writeStoredCurrency,
  type SupportedCurrency,
} from "./quizConstants";

// Restore parsing is deliberately lenient: this hydrates the wizard from a
// possibly-stale/partial saved draft, so a single malformed field should fall
// back to its default rather than aborting the whole restore (`.catch(...)`
// per field, not a top-level throw). `priorityField` is shared with
// quizSchemas.ts so the 0–3 bound stays single-sourced.
const DEFAULT_RESTORE_ANSWERS = {
  disability_mobility: false,
  disability_visual: false,
  disability_hearing: false,
} as const;

const restoreAnswersSchema = z
  .object({
    children_under_12: z.number().int().nullish().catch(null),
    solo_traveller: z.boolean().nullish().catch(null),
    has_disabilities: z.boolean().nullish().catch(null),
    disability_mobility: z.boolean().catch(false),
    disability_visual: z.boolean().catch(false),
    disability_hearing: z.boolean().catch(false),
    priority_wildlife: priorityField.nullish().catch(null),
    priority_authenticity: priorityField.nullish().catch(null),
    priority_service: priorityField.nullish().catch(null),
    priority_eco: priorityField.nullish().catch(null),
    priority_luxury: priorityField.nullish().catch(null),
    priority_value: priorityField.nullish().catch(null),
    budget_pp_per_night: z.number().int().nullish().catch(null),
    budget_currency: z.string().nullish().catch(null),
    budget_flexibility: z.string().nullish().catch(null),
    preferred_country: z.string().nullish().catch(null),
    preferred_area: z.string().nullish().catch(null),
  })
  .catch(DEFAULT_RESTORE_ANSWERS);

// Each top-level field is independently lenient (own `.catch`) so a
// malformed sibling — e.g. a structurally broken `completed_steps` — can't
// clobber an otherwise-parseable `answers` and silently wipe the restore.
const questionnaireDetailSchema = z
  .object({
    id: z.number().optional().catch(undefined),
    status: z.string().optional().catch(undefined),
    completed_steps: z.record(z.string(), z.boolean()).optional().catch(undefined),
    answers: restoreAnswersSchema.default(DEFAULT_RESTORE_ANSWERS),
    prefilled: z.boolean().optional().catch(undefined),
  })
  .catch({ answers: DEFAULT_RESTORE_ANSWERS });

// Shown when a step submit's fetch rejects outright (offline/DNS/CORS) rather than
// resolving with a non-ok HTTP response — see submitStep/submitComplete catches below.
const SUBMIT_NETWORK_ERROR =
  "Something went wrong. Please check your connection and try again.";

// Classifies a quiz-step error so the error card (QuizWizard.tsx) can offer an
// action that actually matches what the error needs, instead of one generic
// "try again" that lies for validation errors (resubmitting the same rejected
// payload can't help) and dead ends (the session/instance itself is gone —
// there's nothing left to retry, only a fresh quiz).
//   - "retryable": a network blip or opaque server error — re-dispatching the
//     same operation (via retryLastSubmit, below) is the right recovery.
//   - "validation": the server rejected the payload — the field list is the
//     instruction, not a button.
//   - "session_dead": transparent session recovery (recoverSession, above)
//     itself failed.
//   - "no_instance": there's no questionnaireId to submit against at all.
export type QuizErrorKind = "retryable" | "validation" | "session_dead" | "no_instance";

function getQuestionnaireId(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  if ("id" in body && typeof body.id === "number") return body.id;
  return null;
}

function isSessionMissingError(status: number, body: unknown): boolean {
  if (status !== 400 && status !== 404) return false;
  if (!body || typeof body !== "object") return false;
  const detail = "detail" in body ? body.detail : undefined;
  if (typeof detail !== "string") return false;
  const text = detail.toLowerCase();
  return (
    text.includes("anonymous session not provided") ||
    text.includes("anonymous session not found") ||
    text.includes("session cookie expired") ||
    text.includes("invalid session cookie")
  );
}

// Submit-path variant of isSessionMissingError: the questionnaire is scoped to
// the anon session, so when the session dies mid-quiz (Safari resumed a stale
// tab, ITP purged the cookie) a step PATCH/complete POST can also come back as
// 404 "Questionnaire instance not found". Both mean the same thing here: the
// server-side context is gone and must be recreated + replayed from in-memory
// answers. init() keeps the narrower matcher — at init time a questionnaire
// 404 can't happen (init creates one).
function isDeadSessionOrQuestionnaireError(status: number, body: unknown): boolean {
  if (isSessionMissingError(status, body)) return true;
  if (status !== 404) return false;
  if (!body || typeof body !== "object") return false;
  const detail = "detail" in body ? body.detail : undefined;
  if (typeof detail !== "string") return false;
  return detail.toLowerCase().includes("questionnaire instance not found");
}

// Shown when transparent session recovery itself fails (e.g. the new session
// POST is rejected) — the user can still manually retry.
const SESSION_RECOVERY_FAILED_ERROR =
  "Your session expired and we couldn't restore it automatically. Please try again.";

// Shown when a questionnaire_not_in_progress error is confirmed NOT to be a
// lost-response-against-a-completed-instance (see tryRecoverNotInProgress) —
// the instance itself is dead (e.g. ABANDONED via session_claim.py's mid-quiz
// login conflict) and there's nothing left to retry, only a fresh quiz.
// Distinct from SESSION_RECOVERY_FAILED_ERROR above, which is about the
// anonymous session failing to recreate, not the instance being unusable.
const INSTANCE_UNRECOVERABLE_ERROR = "This quiz can no longer be continued. Please start a new one.";

type QuizStartFailureReason = "session_missing" | "turnstile" | "network" | "server" | "unknown";

/**
 * Buckets a non-ok API response from init() for `quiz_start_failed`'s `reason`
 * property. Mirrors EnquireModal.tsx's classifyEnquiryFailure, but works off
 * the raw status/body pair clientFetch returns (init() never throws an
 * ApiError — it branches on res.ok itself) rather than a thrown ApiError.
 * `"turnstile"` (the widget rejecting outright) and `"network"` (the outer
 * catch — a rejected fetch, not a non-ok response) are set directly at their
 * own call sites instead of going through this classifier.
 */
function classifyQuizStartApiFailure(status: number, body: unknown): QuizStartFailureReason {
  if (isSessionMissingError(status, body)) return "session_missing";
  if (status >= 500) return "server";
  return "unknown";
}

/**
 * Distinguishes a genuine network failure from any other throw reaching init()'s outer
 * catch, for `quiz_start_failed`'s `reason` there. `fetch()` itself rejects with a
 * `TypeError` on a network failure (offline/DNS/CORS); `clientFetch`'s own
 * `AbortSignal.timeout()` (lib/api/client.ts) instead rejects with a `DOMException` named
 * "TimeoutError" — not a `TypeError` — so checking `instanceof TypeError` alone would
 * misclassify a genuine request timeout as "unknown".
 */
function isNetworkLikeError(e: unknown): boolean {
  if (e instanceof TypeError) return true;
  if (e instanceof DOMException && (e.name === "TimeoutError" || e.name === "AbortError")) {
    return true;
  }
  return false;
}

// fetch() rejects with a plain TypeError specifically on a dropped
// connection (offline, a tunnel, a Wi-Fi↔cellular handoff) — the failure mode
// behind the PostHog replay this ticket came from (`TypeError: Load failed`).
// It fails in milliseconds and very often resolves itself a second later, so
// it's the only failure class worth retrying automatically (see
// withDroppedConnectionRetry below). Deliberately narrower than
// isNetworkLikeError: a timeout means clientFetch's own 30s
// AbortSignal.timeout has already elapsed — the user has waited long enough,
// automatically doubling that is worse UX than just showing the card.
//
// This can't distinguish "dropped connection" from a genuine
// programming TypeError anywhere inside the wrapped call (patchStep/
// postComplete wrap all of clientFetch, not just the fetch() call itself) —
// such a bug would be silently retried once and then misreported to the user
// as a connection problem. Accepted: narrowing further means editing shared
// lib/api/client.ts, out of proportion to how rare/low-impact that is.
function isDroppedConnectionError(e: unknown): boolean {
  return e instanceof TypeError;
}

const RETRY_DELAY_MS = 1000;

// Wraps a single network call with one silent retry when it fails with a
// dropped connection. Deliberately scoped to the call itself (patchStep /
// postComplete below), not to the callers' whole try blocks — those contain
// one-shot side effects (safeCapture, trackMetaEvent, router.push) that must
// never fire twice. The retry is invisible by construction: callers are
// already inside their own busy/submitting state for the duration, so this
// just makes that state last ~1s longer on the rare case it's needed.
//
// `label` identifies which call this is guarding ("step:weight_priorities",
// "step:constraints", "complete") purely for the telemetry below — TRA-345
// itself came from a PostHog replay with no way to tell whether a fix like
// this one actually engages in production, so a retry that fires emits its
// own event rather than staying invisible end-to-end.
async function withDroppedConnectionRetry<T>(fn: () => Promise<T>, label: string): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    if (!isDroppedConnectionError(e)) throw e;
    safeCapture("quiz_submit_auto_retry", { label });
    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
    return fn();
  }
}

// 429 (throttled) gets the same "try again" recovery as a
// network blip or an opaque 5xx — humanizeApiMessage already renders a 429 as
// "Please wait a moment and try again", so classifying it "validation"
// (no button) would show copy promising an action that isn't there. 408 is
// deliberately not included: nothing in this stack produces it — the proxy's
// own timeout is a 504 (already >= 500, already retryable).
function isRetryableStatus(status: number): boolean {
  return status >= 500 || status === 429;
}

// init()-only retry predicate (TRA-350) — deliberately narrower than
// isRetryableStatus above, which still drives the *submit* error card's
// "retryable" kind. 429 is excluded here: the questionnaire and session POSTs
// share the "questionnaire_create" throttle scope, so retrying a 429 after a
// short delay almost always just earns another 429 and adds to the count —
// worse than leaving it to the existing error-card Retry button.
function isInitRetryableStatus(status: number): boolean {
  return status >= 500;
}

type InitRetryResult = { res: Response; body: unknown };

// Thrown by withInitRetry when isCancelled() flips true mid-ladder (PR #548
// review) — the component unmounted (Back button, fast navigation away from
// /quiz) while a retry's delay or beforeRetry was in flight. init()'s catches
// special-case this: no captureException, no quiz_start_failed, and none of
// the success-path side effects (router.replace, quiz_started, Meta
// QuizStart) run for a component that isn't mounted to see them.
class InitCancelledError extends Error {}

type InitRetryOptions = {
  // Identifies which of init()'s calls this is guarding, purely for the
  // quiz_start_retry telemetry below (mirrors `label` on
  // withDroppedConnectionRetry above). The two POST v1/questionnaires calls
  // get distinct labels ("questionnaire" vs "questionnaire_after_session")
  // so a 5xx on the post-session-creation retry isn't conflated with one on
  // the very first call.
  call: "questionnaire" | "questionnaire_after_session" | "session";
  delaysMs: number[];
  // Absolute Date.now() deadline shared across every call in one init() run —
  // see maxElapsedMs on useQuizWizardController's initRetry option. Bounds
  // the worst case: a hung upstream still costs ~30s per attempt
  // (clientFetch's own timeout), so a naive fixed attempt count alone could
  // stall a user for far longer than this ladder is meant to. Checked against
  // the *next* delay, not just Date.now() — a failure that lands just under
  // the deadline must not still sleep the full next delay and fire one more
  // attempt anyway (PR #548 review).
  deadline: number;
  // Runs between a retryable failure and the next attempt, after the delay.
  // Used only by the session-anonymous call, to get a fresh Turnstile token —
  // tokens are single-use and the proxy verifies before forwarding, so
  // re-POSTing with the same token after a 503 would come back a 403. A
  // retry that escalates to a second interactive Cloudflare checkbox is
  // possible here; accepted, since the user is the one solving it and
  // getToken's own 60s interactive deadline isn't bounded by `deadline`.
  beforeRetry?: () => Promise<void>;
  isCancelled: () => boolean;
};

// Bounded retry ladder for a single init() fetch (TRA-350). Retries only a
// >=500 response (isInitRetryableStatus) or a thrown dropped-connection
// TypeError (isDroppedConnectionError, shared with withDroppedConnectionRetry
// above) — never a 4xx, and never a genuine timeout (AbortSignal.timeout
// rejects with a DOMException, not a TypeError, so it's left alone and
// propagates on the first attempt exactly as before). A `beforeRetry` throw
// (only the session call's fresh-token fetch can do this) propagates
// immediately without being treated as a retryable failure itself.
async function withInitRetry(
  fn: () => Promise<InitRetryResult>,
  { call, delaysMs, deadline, beforeRetry, isCancelled }: InitRetryOptions,
): Promise<InitRetryResult> {
  const maxAttempts = delaysMs.length + 1;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    let result: InitRetryResult | undefined;
    let threw = false;
    let thrown: unknown;
    try {
      result = await fn();
    } catch (e) {
      threw = true;
      thrown = e;
    }

    if (isCancelled()) throw new InitCancelledError();

    const isLastAttempt = attempt === maxAttempts;
    const delayMs = delaysMs[attempt - 1] ?? 0;
    // Checked against the delay that's about to run, not just "now" — a
    // failure landing just under the deadline would otherwise still sleep
    // the full delay and fire one more attempt past it (PR #548 review).
    const pastDeadline = Date.now() + delayMs >= deadline;
    let retryReason: "network" | "server" | undefined;

    if (threw) {
      if (!isDroppedConnectionError(thrown) || isLastAttempt || pastDeadline) throw thrown;
      retryReason = "network";
    } else if (result!.res.ok || !isInitRetryableStatus(result!.res.status) || isLastAttempt || pastDeadline) {
      return result!;
    } else {
      retryReason = "server";
    }

    await new Promise((resolve) => setTimeout(resolve, delayMs));
    if (isCancelled()) throw new InitCancelledError();
    await beforeRetry?.();
    if (isCancelled()) throw new InitCancelledError();
    // Captured only once the retry is actually about to happen — not before
    // the delay, where a beforeRetry failure (e.g. a rejected getToken on
    // the session call) or a cancellation would report a retry that never
    // ran (PR #548 review).
    safeCapture("quiz_start_retry", { attempt: attempt + 1, reason: retryReason, call });
  }
  // Unreachable — the loop above always returns or throws on its last
  // iteration (isLastAttempt is true then).
  throw new Error("withInitRetry: exhausted attempts without a result");
}

const INIT_RETRY_DELAYS_MS = [1000, 2000];
const INIT_RETRY_MAX_ELAPSED_MS = 10_000;

function deriveStep(
  completedSteps: Record<string, boolean> | undefined,
  prefilled: boolean | undefined,
): number {
  // A prefilled instance has both steps marked complete before the user has
  // touched anything — land on step 1 so priorities aren't
  // silently skipped, same as a genuinely blank quiz.
  if (prefilled) return 1;
  if (!completedSteps?.weight_priorities) return 1;
  return 2; // never auto-advance to Review; completed quizzes redirect in init()
}

// A repeat anonymous quiz start refused by TRA-338's login_required gate
// (POST /v1/questionnaires -> 403 {detail, code: "login_required"}). Narrowed
// on `code`, not just status: the proxy's own Turnstile rejection is also a
// plain 403 with a `detail` and no `code` (route.ts), and must not be
// misread as this gate.
function isLoginRequiredError(status: number, body: unknown): boolean {
  if (status !== 403) return false;
  return normalizeDrfError(body, status).code === "login_required";
}

export type QuizInitState = "idle" | "loading" | "ready" | "error" | "auth_required";

export type QuizWizardController = {
  initState: QuizInitState;
  step: number;
  stepImageOrders: Record<QuizStepKey, StepImageEntry[]>;
  trip: TripProfileDraft;
  priorities: PrioritiesDraft;
  constraints: ConstraintsDraft;
  geoOptions: GeographyOptions | null;
  geoError: boolean;
  priorityDescriptions: PriorityDescriptions;
  busyOperation: StepKey | "complete" | null;
  submitting: boolean;
  error: string | null;
  errorKind: QuizErrorKind | null;
  // Bumped on every new error, even when the message/kind is identical to the
  // last one (e.g. two consecutive dropped-connection failures) — the error
  // card (QuizWizard.tsx) watches this to re-trigger scroll-into-view/focus,
  // which a same-value error/errorKind change alone wouldn't do.
  errorNonce: number;
  fieldErrors: Record<string, string[]> | undefined;
  missingRequired: Record<string, string[]> | undefined;
  completed: boolean;
  canContinue: boolean;
  inputsLocked: boolean;
  onNext: () => Promise<void>;
  onSubmitSkipReview: () => Promise<void>;
  onChangeTrip: (next: TripProfileDraft) => void;
  onBack: (() => void) | undefined;
  onSubmit: () => Promise<void>;
  onEdit: (target: 1 | 2) => void;
  restartQuiz: () => Promise<void>;
  // Re-dispatches whichever of onNext/onSubmitSkipReview/onSubmit last failed,
  // against current form state — not the payload captured at failure time, so
  // an edit made after the error is what actually gets submitted (see
  // errorKind === "retryable" in the error card).
  retryLastSubmit: () => Promise<void>;
  setPriorities: (p: PrioritiesDraft) => void;
  setConstraints: (c: ConstraintsDraft) => void;
};

type QuizWizardControllerOptions = {
  getToken?: () => Promise<string>;
  resetToken?: () => void;
  // Test-only override for the init() retry ladder's timing (TRA-350) —
  // production callers never pass this, so they always get the real
  // INIT_RETRY_DELAYS_MS / INIT_RETRY_MAX_ELAPSED_MS. Kept injectable rather
  // than switching this file to fake timers: withDroppedConnectionRetry's
  // existing tests already sleep for real under the default vitest
  // testTimeout, and a 3-attempt ladder on top of that would blow it.
  initRetry?: { delaysMs?: number[]; maxElapsedMs?: number };
};

export function useQuizWizardController(
  opts: QuizWizardControllerOptions = {},
): QuizWizardController {
  const { getToken, resetToken, initRetry } = opts;
  const initRetryDelaysMs = initRetry?.delaysMs ?? INIT_RETRY_DELAYS_MS;
  const initRetryMaxElapsedMs = initRetry?.maxElapsedMs ?? INIT_RETRY_MAX_ELAPSED_MS;
  const router = useRouter();
  const [step, setStep] = useState(1);

  const [questionnaireId, setQuestionnaireId] = useState<number | null>(null);
  const [initState, setInitState] = useState<QuizInitState>("idle");
  const [busyOperation, setBusyOperation] = useState<StepKey | "complete" | null>(null);
  // Shows the branded splash the instant submit starts, before the awaited
  // network calls — otherwise the form sits static for the round-trip(s).
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<QuizErrorKind | null>(null);
  const [errorNonce, setErrorNonce] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | undefined>(undefined);
  const [missingRequired, setMissingRequired] = useState<Record<string, string[]> | undefined>(undefined);
  const [completed, setCompleted] = useState(false);
  // Which user-initiated submit dispatcher was last invoked, so
  // retryLastSubmit can re-invoke the right one. Stores a tag rather than the
  // function itself, so it always resolves through onNext/onSubmitSkipReview/
  // onSubmit's OWN latest closure (current step/canContinue/etc.) instead of
  // whatever those closed over back when this ref was written. A ref, not
  // state, since it's write-only from render's perspective.
  const lastSubmitRef = useRef<"onNext" | "onSubmitSkipReview" | "onSubmit" | null>(null);

  const [trip, setTrip] = useState<TripProfileDraft>({
    childrenUnder12: null,
    soloTraveller: null,
    hasDisabilities: null,
    disabilityMobility: false,
    disabilityVisual: false,
    disabilityHearing: false,
  });
  const [priorities, setPriorities] = useState<PrioritiesDraft>({
    wildlife: 2,
    authenticity: 2,
    service: 2,
    eco: 1,
    luxury: 1,
    value: 1,
  });

  // SSR-safe: start with USD so server and client render identically; detect real
  // currency on the client in the effect below, before any API restore can arrive.
  const currencyDetectedRef = useRef(false);
  const submitInFlightRef = useRef(false);
  const initStartedRef = useRef(false);
  // True while the component that started this init() run is still mounted
  // (PR #548 review). The retry ladder can now hold init() open for up to
  // ~10s; without this, a user who navigates away mid-ladder (Back button,
  // a fast route change) would still get a late success's side effects —
  // router.replace to /quiz/results, quiz_started, Meta QuizStart — running
  // against a component nobody's looking at. Set true just before the first
  // init() call and false in that effect's cleanup; StrictMode's dev-only
  // mount→cleanup→mount leaves it true again before any await resolves, so
  // it doesn't false-cancel a real first run. Separate from initStartedRef,
  // which only ever prevents a *second* init() call from starting.
  const initAliveRef = useRef(false);
  // Caps the login_required gate at one shown per mount — deliberately NOT
  // reset in restartQuiz(). If the post-auth session bridge fails (e.g. a
  // DJANGO_SESSION_COOKIE_NAME mismatch), the retry POST is still anonymous
  // and would otherwise get gated again with fresh state, trapping the
  // visitor in a "sign up" -> "email already registered" loop with no route
  // out. A second login_required 403 in the same mount falls through to the
  // ordinary error path instead.
  const authGateShownRef = useRef(false);
  const [constraints, setConstraints] = useState<ConstraintsDraft>({
    budget: null,
    currency: "USD",
    budgetFlexibility: DEFAULT_BUDGET_FLEXIBILITY,
    preferredCountry: null,
    preferredRegion: null,
  });

  // Geography (country/region) options are static reference data — fetched once
  // here (this hook stays mounted for the whole wizard lifetime) and shared with
  // both Constraints and Review, rather than each step fetching independently.
  // On failure the restored preferredCountry/preferredRegion are left untouched
  // (a transient options-list failure must not silently discard a backend-saved
  // preference); geoError just disables the picker / falls back to a humanized
  // label. Aborts (StrictMode double-invoke, fast unmount) are not real failures.
  const [geoOptions, setGeoOptions] = useState<GeographyOptions | null>(null);
  const [geoError, setGeoError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchGeographyOptions({ signal: controller.signal })
      .then((opts) => setGeoOptions(opts))
      .catch((e) => {
        if (e instanceof DOMException && e.name === "AbortError") return;
        setGeoError(true);
      });
    return () => controller.abort();
  }, []);

  // Info-icon copy for quiz step 1's priority sliders. Non-essential content —
  // unlike geography, there is no error state: any rejection (network failure,
  // abort, malformed body) just leaves this at {}, and QuizStepPriorities'
  // InfoTooltip already renders nothing for a missing key, so the quiz degrades
  // to "no icon" for that row rather than surfacing an error.
  const [priorityDescriptions, setPriorityDescriptions] = useState<PriorityDescriptions>({});

  useEffect(() => {
    const controller = new AbortController();
    fetchPriorityDescriptions({ signal: controller.signal })
      .then((descriptions) => setPriorityDescriptions(descriptions))
      .catch(() => {
        // Swallowed deliberately — see comment above.
      });
    return () => controller.abort();
  }, []);

  const [createRequestId] = useState(() => crypto.randomUUID());

  // Seeded once per quiz session; stable across step navigation because this
  // hook stays mounted for the full wizard lifetime. useState (not useMemo —
  // React doesn't guarantee to preserve a memo across renders) for the same
  // one-time-value reason as createRequestId above. Reading readLastShownSrc
  // here is fine (idempotent, side-effect-free); writing this visit's first
  // image back happens in the effect below, not here — a useState
  // initializer is expected to be pure, and React invokes it twice under
  // StrictMode dev, discarding one result (the same class of bug already
  // fixed in the hero carousel's setActiveIndex updater). Each step's
  // QuizStepImageCarousel takes its order from here and then owns all
  // subsequent reshuffling itself — this hook only computes the starting
  // point, same as Hero.tsx does for the marketing carousel.
  // The full shuffled 40-entry pool is passed through — not a pre-sliced
  // subset. Bounding the session's worst case now lives in
  // QuizStepImageCarousel's MOUNT_WINDOW (a *mount* cap, keeping only a few
  // <Image> elements alive at once), not here, so a visitor who lingers
  // through the whole carousel rotates all 40 photos instead of a smaller
  // subset repeating after ~50s.
  //
  // The tradeoff: MOUNT_WINDOW bounds decoded bitmap memory, not bytes over
  // the wire, so a session that dwells past ~50s on a step downloads up to 40
  // images rather than a smaller capped subset. A median session (well under
  // 50s) is unaffected either way, since QuizStepImageCarousel only ever
  // fetches a slide once it's mounted.
  const [stepImageOrders] = useState<Record<QuizStepKey, StepImageEntry[]>>(() => ({
    priorities: shuffleImagePool(STEP_IMAGES.priorities, Math.random, readLastShownSrc("priorities")),
    constraints: shuffleImagePool(STEP_IMAGES.constraints, Math.random, readLastShownSrc("constraints")),
  }));

  useEffect(() => {
    writeLastShownSrc("priorities", stepImageOrders.priorities[0]!.src);
    writeLastShownSrc("constraints", stepImageOrders.constraints[0]!.src);
    // stepImageOrders is set once via useState above and never changes, so
    // listing its fields here is equivalent to an empty deps array in
    // practice — this effect only ever runs once, on mount.
  }, [stepImageOrders.priorities, stepImageOrders.constraints]);

  function clearErrors() {
    setError(null);
    setErrorKind(null);
    setFieldErrors(undefined);
    setMissingRequired(undefined);
  }

  // Sets an error and bumps errorNonce so the error card always re-triggers
  // its scroll/focus behavior, even when message+kind are unchanged from the
  // last error (two dropped-connection failures in a row, say).
  function setQuizError(message: string, kind: QuizErrorKind) {
    setError(message);
    setErrorKind(kind);
    setErrorNonce((n) => n + 1);
  }

  // Normalizes an API error response and stores it in component state.
  // Returns false so callers can write: if (!res.ok) return applyApiError(res.status, body);
  function applyApiError(status: number, body: unknown): false {
    const norm = normalizeDrfError(body, status);
    // Humanize only the headline message — field errors are already readable.
    setQuizError(
      humanizeApiMessage(norm.message, status, norm.code),
      // A 5xx or a 429 is the server's own state, not a rejected payload —
      // the same "try again" recovery as a network blip (isRetryableStatus).
      // Everything else (4xx) is either a rejected payload or a
      // business-logic conflict; retrying the identical request can't help
      // either, so it's "validation" (no action button — see the error card
      // in QuizWizard.tsx).
      isRetryableStatus(status) ? "retryable" : "validation",
    );
    setFieldErrors(norm.fieldErrors);
    setMissingRequired(norm.missingRequiredFields);
    return false;
  }

  async function init() {
    if (initStartedRef.current) return;
    initStartedRef.current = true;
    setInitState("loading");
    clearErrors();
    setCompleted(false);
    // Shared across every fetch in this init() run (TRA-350) — see
    // InitRetryOptions.deadline. Computed once per run, not per call, so a
    // retry on the first questionnaire POST can't leave the session POST a
    // fresh 10s budget of its own.
    const initRetryDeadline = Date.now() + initRetryMaxElapsedMs;
    try {
      // 1) Create or resume questionnaire with existing session first. No Turnstile
      // token is requested for this call — a returning visitor with a valid session
      // cookie resolves here and must never see a challenge just for loading /quiz.
      let questionnaireRes = await withInitRetry(
        () =>
          clientFetch(proxyPath("v1/questionnaires"), {
            method: "POST",
            body: JSON.stringify({
              request_id: createRequestId,
            }),
          }),
        {
          call: "questionnaire",
          delaysMs: initRetryDelaysMs,
          deadline: initRetryDeadline,
          isCancelled: () => !initAliveRef.current,
        },
      );
      // The retry ladder can hold this call open for several seconds — bail
      // before any side effect below if the component that started this
      // init() run isn't mounted anymore (PR #548 review). withInitRetry
      // itself already throws InitCancelledError for a cancellation *during*
      // the ladder; this covers the gap between the ladder's last await
      // resolving and this line.
      if (!initAliveRef.current) return;
      if (!questionnaireRes.res.ok && isSessionMissingError(questionnaireRes.res.status, questionnaireRes.body)) {
        // 2) Only a first-time/expired-session visitor reaches here. Fetch the
        // Turnstile token now — under execution: "execute" this is what actually
        // triggers the challenge, so it must stay gated on genuinely needing a new
        // session rather than firing eagerly on every mount. The extra latency is
        // absorbed by QuizWizard's minimum splash, MIN_SPLASH_MS (QuizWizard.tsx), so serializing
        // it after the first POST (rather than racing it in parallel) costs nothing
        // visible.
        let turnstileToken = "";
        // Tracks whether a thrown error below came from acquiring a token
        // (this function) rather than from withInitRetry's own fn() — see the
        // catch around the withInitRetry call below, which needs to tell the
        // two apart to pick the right quiz_start_failed reason.
        let turnstileAcquireFailed = false;
        async function acquireToken() {
          try {
            turnstileToken = getToken ? await getToken() : "";
          } catch (e) {
            turnstileAcquireFailed = true;
            throw e;
          }
        }
        try {
          await acquireToken();
        } catch (e) {
          setError(e instanceof Error ? e.message : "Security check failed. Please try again.");
          setInitState("error");
          safeCapture("quiz_start_failed", { reason: "turnstile" });
          return;
        }
        let sessionRes: InitRetryResult;
        try {
          sessionRes = await withInitRetry(
            () =>
              clientFetch(proxyPath("v1/sessions/anonymous"), {
                method: "POST",
                body: JSON.stringify({}),
                ...(turnstileToken ? { headers: { "cf-turnstile-token": turnstileToken } } : {}),
              }),
            {
              call: "session",
              delaysMs: initRetryDelaysMs,
              deadline: initRetryDeadline,
              // Tokens are single-use and the proxy verifies before
              // forwarding (see AGENTS.md's Turnstile section) — re-POSTing
              // a retry with the same token after a 503 would come back a
              // proxy 403, not a real retry.
              beforeRetry: async () => {
                resetToken?.();
                await acquireToken();
              },
              isCancelled: () => !initAliveRef.current,
            },
          );
        } catch (e) {
          if (e instanceof InitCancelledError) return;
          if (turnstileAcquireFailed) {
            setError(e instanceof Error ? e.message : "Security check failed. Please try again.");
            setInitState("error");
            safeCapture("quiz_start_failed", { reason: "turnstile" });
            return;
          }
          throw e;
        }
        if (!initAliveRef.current) return;
        if (!sessionRes.res.ok) {
          applyApiError(sessionRes.res.status, sessionRes.body);
          setInitState("error");
          safeCapture("quiz_start_failed", {
            reason: classifyQuizStartApiFailure(sessionRes.res.status, sessionRes.body),
          });
          return;
        }
        // Token consumed — reset so the next visit gets a fresh one.
        resetToken?.();
        questionnaireRes = await withInitRetry(
          () =>
            clientFetch(proxyPath("v1/questionnaires"), {
              method: "POST",
              body: JSON.stringify({
                request_id: createRequestId,
              }),
            }),
          {
            call: "questionnaire_after_session",
            delaysMs: initRetryDelaysMs,
            deadline: initRetryDeadline,
            isCancelled: () => !initAliveRef.current,
          },
        );
        if (!initAliveRef.current) return;
      }
      // Step 1 succeeded (returning visitor) or a non-session-missing error — no
      // Turnstile token was ever requested for this visitor, matching the comment
      // at the top of this function.
      //
      // Checked after the session-create retry above so a login_required 403 on
      // *either* attempt is caught here — TRA-339/340: an anon session that
      // already completed a quiz with results is refused a second one. This is
      // its own state (not "error") and its own events, not a quiz_start_failed:
      // nothing actually failed, the visitor just needs to authenticate first.
      //
      // Gated on authGateShownRef so this fires at most once per mount — see
      // its declaration above. A second login_required 403 (e.g. from restartQuiz()
      // after a failed post-auth session bridge) falls through to the
      // generic !questionnaireRes.res.ok branch below instead of reopening the gate.
      if (!questionnaireRes.res.ok && isLoginRequiredError(questionnaireRes.res.status, questionnaireRes.body) && !authGateShownRef.current) {
        authGateShownRef.current = true;
        setInitState("auth_required");
        safeCapture("quiz_auth_gate_shown");
        return;
      }
      if (!questionnaireRes.res.ok) {
        applyApiError(questionnaireRes.res.status, questionnaireRes.body);
        setInitState("error");
        safeCapture("quiz_start_failed", {
          reason: classifyQuizStartApiFailure(questionnaireRes.res.status, questionnaireRes.body),
        });
        return;
      }

      const id = getQuestionnaireId(questionnaireRes.body);
      if (!id) {
        setError("Questionnaire created but no instance id was returned.");
        setInitState("error");
        safeCapture("quiz_start_failed", { reason: "unknown" });
        return;
      }

      setQuestionnaireId(id);
      // The create/resume POST above now returns the full questionnaire detail
      // (including `answers`) — see Django `get_or_create_active_instance` — so no
      // separate GET is needed to restore the draft. This removes one serialized
      // round-trip from every quiz open, not just the first-visit path.
      const detail = questionnaireDetailSchema.parse(questionnaireRes.body ?? {});
      const answers = detail.answers;
      setTrip({
        childrenUnder12: answers.children_under_12 ?? null,
        soloTraveller: answers.solo_traveller ?? null,
        hasDisabilities: answers.has_disabilities ?? null,
        disabilityMobility: answers.disability_mobility ?? false,
        disabilityVisual: answers.disability_visual ?? false,
        disabilityHearing: answers.disability_hearing ?? false,
      });
      setPriorities({
        wildlife: answers.priority_wildlife ?? 2,
        authenticity: answers.priority_authenticity ?? 2,
        service: answers.priority_service ?? 2,
        eco: answers.priority_eco ?? 1,
        luxury: answers.priority_luxury ?? 1,
        value: answers.priority_value ?? 1,
      });
      currencyDetectedRef.current = true;
      // budget_currency is non-null with a model default of "USD" on the Django side, so a
      // brand-new response echoes "USD" even though nobody chose it. The
      // constraints step PATCH is currently the only path that writes budget_currency, and it
      // always sends the full payload, so completed_steps.constraints tracks it — but that's
      // an artifact of today's FE, not a backend guarantee (budget_currency isn't in
      // REQUIRED_FIELDS_BY_STEP). Re-check this gate if a partial constraints save is ever
      // introduced. budget_pp_per_night is gated the same way: an ungated budget from an
      // untrusted currency could get clamped into the wrong CURRENCY_CONFIG range.
      const constraintsSaved = detail.completed_steps?.constraints === true;
      const restoredCurrency: SupportedCurrency =
        constraintsSaved && isSupportedCurrency(answers.budget_currency)
          ? answers.budget_currency
          : resolveInitialCurrency();
      const restoredCfg = CURRENCY_CONFIG[restoredCurrency]!;
      const restoredBudget =
        constraintsSaved && answers.budget_pp_per_night != null
          ? Math.min(Math.max(answers.budget_pp_per_night, restoredCfg.min), restoredCfg.max)
          : null;
      setConstraints({
        budget: restoredBudget,
        currency: restoredCurrency,
        // Guarded like currency above (this is a full object replace, so an
        // omitted field here would wipe the draft on every resume): a saved
        // "target" restores; anything else falls back to the "max" default.
        budgetFlexibility: isBudgetFlexibility(answers.budget_flexibility)
          ? answers.budget_flexibility
          : DEFAULT_BUDGET_FLEXIBILITY,
        preferredCountry: answers.preferred_country ?? null,
        preferredRegion: answers.preferred_area ?? null,
      });
      setStep(deriveStep(detail.completed_steps, detail.prefilled));

      if (detail.status === "completed") {
        setCompleted(true);
        // replace, not push: this redirect substitutes the current history entry.
        // With push, Back from results lands on /quiz, init() re-runs and pushes
        // forward again — trapping the back button.
        router.replace(`/quiz/results/${id}`);
        return;
      }
      // Meta's QuizStart still dedupes per questionnaire id —
      // that's for ad attribution and must not double-fire across reloads/resumes.
      // PostHog's quiz_started is NOT deduped the same way: the quiz funnel dashboard
      // aggregates by session (identity fragments under TRA-284's cookieless-until-consent
      // model, so per-person aggregation undercounts), and PostHog already collapses
      // repeated events within one session to a single funnel step — an id-keyed dedupe
      // here would only suppress genuine next-day resumes, which is exactly the case the
      // funnel needs to see. `is_resume` distinguishes the two at query time instead — but
      // it's not the "was this a genuinely new start" signal (a reload before any step is
      // submitted also reports is_resume: false, since completed_steps.weight_priorities
      // is still unset). `is_first_start` carries markQuizStarted's own answer to that
      // question instead of discarding it.
      const isFirstStart = markQuizStarted(id);
      if (isFirstStart) {
        trackMetaEvent(META_PIXEL_EVENT.quizStart);
      }
      safeCapture("quiz_started", {
        // A prefilled instance arrives with both steps already marked
        // complete before the user has touched anything — deriveStep() above checks
        // `prefilled` before `completed_steps` for exactly this reason, and this must
        // match it, or the first-ever start of a prefilled quiz reports is_resume: true.
        is_resume: !detail.prefilled && Boolean(detail.completed_steps?.weight_priorities),
        is_first_start: isFirstStart,
        questionnaire_id: id,
      });
      setInitState("ready");
    } catch (e) {
      // A cancellation (component unmounted mid-ladder — PR #548 review) is
      // not a real failure: no captureException, no quiz_start_failed, and
      // definitely no state update against an unmounted component.
      if (e instanceof InitCancelledError) return;
      // This catch is init()'s catch-all, not a fetch-rejection handler — resetToken?.()
      // and the completed-questionnaire router.replace() above both sit inside this same
      // try, so a genuinely unexpected throw from either would land here too. Classify
      // rather than assume "network". A throw from the completed-redirect
      // path would misreport quiz_start_failed for a user who actually finished, but the
      // reason would correctly read "unknown", not "network" — not worth a special case.
      posthog.captureException(e);
      setError(e instanceof Error ? e.message : "Failed to initialize quiz.");
      setInitState("error");
      safeCapture("quiz_start_failed", { reason: isNetworkLikeError(e) ? "network" : "unknown" });
    }
  }

  useEffect(() => {
    initAliveRef.current = true;
    void init();
    return () => {
      initAliveRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect real locale/stored currency client-side after hydration. Runs once on
  // mount, before the async init() network response can arrive, so it never races
  // with the API restore path (which sets currencyDetectedRef.current = true first).
  useEffect(() => {
    if (currencyDetectedRef.current) return;
    currencyDetectedRef.current = true;
    const c = resolveInitialCurrency();
    if (c === "USD") return;
    // Only swap the currency while the budget is still untouched — never
    // substitute a budget value here, so the user still has to touch the
    // slider on whichever currency they end up on.
    setConstraints((prev) =>
      prev.currency === "USD" && prev.budget === null ? { ...prev, currency: c } : prev,
    );
  }, []);

  // Mirrors constraints.currency outside the setState call so handleChangeConstraints
  // can compare against it without putting a side effect inside a setState updater
  // (updaters must stay pure — StrictMode double-invokes them).
  const currencyRef = useRef<SupportedCurrency>(constraints.currency);
  useEffect(() => {
    currencyRef.current = constraints.currency;
  }, [constraints.currency]);

  // Wrapped setter for the public `setConstraints`: only an explicit user currency
  // change is persisted to localStorage. init()'s restore and the detect
  // effect above both call the raw `setConstraints` internally, so they never write
  // storage — a stored value now means "the user picked this", which is what lets
  // resolveInitialCurrency() trust it on the next visit.
  const handleChangeConstraints = useCallback((next: ConstraintsDraft) => {
    if (next.currency !== currencyRef.current) {
      writeStoredCurrency(next.currency);
    }
    setConstraints(next);
  }, []);

  // The two step payloads are built from state in one place so the normal
  // submit path and the session-recovery replay path can't drift apart.
  function buildPrioritiesPayload() {
    return {
      priority_wildlife: priorities.wildlife,
      priority_authenticity: priorities.authenticity,
      priority_service: priorities.service,
      priority_eco: priorities.eco,
      priority_luxury: priorities.luxury,
      priority_value: priorities.value,
    };
  }

  function buildConstraintsPayload() {
    // constraints.budget is non-null by the time this runs, but NOT because
    // canContinue guards every call site — it doesn't; canContinue is
    // unconditionally true at step 3 (see below), and submitComplete()'s
    // dead-session replay calls this again from there. The real guarantee is
    // deriveStep() above: it never returns 3, so step 3 (and therefore this
    // replay path) is only ever reached via onNext()'s canContinue-gated
    // patchConstraints() call at step 2. If deriveStep ever learns to resume
    // straight into step 3 (e.g. for prefilled quizzes), that would break
    // this invariant too — re-check both call sites then.
    return {
      budget_pp_per_night: constraints.budget,
      budget_currency: constraints.currency,
      budget_flexibility: constraints.budgetFlexibility,
      children_under_12: trip.childrenUnder12,
      solo_traveller: trip.soloTraveller,
      has_disabilities: trip.hasDisabilities,
      disability_mobility: trip.disabilityMobility,
      disability_visual: trip.disabilityVisual,
      disability_hearing: trip.disabilityHearing,
      // Destination filter is paused (SHOW_PREFERRED_DESTINATION) — force null
      // here so a resumed questionnaire with a saved preference can't silently
      // re-send a destination the user no longer sees or can clear.
      preferred_country: SHOW_PREFERRED_DESTINATION ? constraints.preferredCountry : null,
      preferred_area: SHOW_PREFERRED_DESTINATION ? constraints.preferredRegion : null,
    };
  }

  // request_id is now stable per (scope, payload) rather than freshly
  // minted on every call. Django's idempotency (QUESTIONNAIRE_IDEMPOTENCY_ENFORCED,
  // default True — questionnaire_service.py) is keyed on exactly this id, so a
  // fresh id per attempt meant a retry after a *lost response* (the request
  // reached Django and succeeded, but the client never saw it — a dropped
  // connection or the 30s clientFetch timeout) looked like a brand-new request:
  // harmless for a step PATCH (Django just re-saves the same answers), but for
  // /complete it hits _resolve_completed_status_gate's "Questionnaire is not in
  // progress" — the instance is already COMPLETED under the old request_id, so
  // the new one is rejected outright, stranding the user from results that
  // already exist server-side (see the questionnaire_not_in_progress recovery
  // in submitComplete below). Reusing the same id while the payload is
  // unchanged makes a retry replay into Django's idempotency cache instead
  // (same response back) — and regenerating it the moment the payload changes
  // avoids the opposite failure, an idempotency_conflict against a stale id.
  //
  // Keyed by questionnaire id (part of `scope`) so recoverSession() minting a
  // new instance naturally gets fresh ids with no explicit reset needed.
  const requestIdsRef = useRef(new Map<string, { hash: string; id: string }>());
  function requestIdFor(scope: string, payload: unknown): string {
    const hash = JSON.stringify(payload ?? null);
    const cur = requestIdsRef.current.get(scope);
    if (cur && cur.hash === hash) return cur.id;
    const id = crypto.randomUUID();
    requestIdsRef.current.set(scope, { hash, id });
    return id;
  }

  function patchStep(id: number, stepKey: StepKey, payload: unknown) {
    const request_id = requestIdFor(`${id}:step:${stepKey}`, payload);
    return withDroppedConnectionRetry(
      () =>
        clientFetch(proxyPath(`v1/questionnaires/${id}/steps/${stepKey}`), {
          method: "PATCH",
          body: JSON.stringify({ request_id, payload }),
        }),
      `step:${stepKey}`,
    );
  }

  // questionnaire_not_in_progress fires for two very different
  // server states that the response body alone can't distinguish —
  //   - a replay against a COMPLETED instance under a DIFFERENT request_id
  //     than the one that completed it (another tab/session, or a completion
  //     recorded before this PR's stable-request_id fix existed) —
  //     _resolve_completed_status_gate (questionnaire_service.py) only
  //     replays 200 when the ids match, otherwise raises this same code
  //   - an ABANDONED instance: session_claim.py's mid-quiz login conflict
  //     marks the OLDER of two same-version in-progress instances (across the
  //     anon session and the now-logged-in user) ABANDONED, and its own
  //     docstring says further writes on it fail with exactly this code —
  //     this is the far more reachable case in production, and it has NO
  //     recommendation run at all
  // Those need opposite UI — navigate to results for the first, offer a
  // fresh quiz for the second, since there's nothing left to retry on a dead
  // instance — and the only way to tell them apart is to ask: a plain GET
  // for the recommendations that would exist if this really did complete.
  async function tryRecoverNotInProgress(instanceId: number): Promise<boolean> {
    try {
      const { res } = await clientFetch(
        proxyPath(`v1/questionnaires/${instanceId}/recommendations`),
      );
      if (res.ok) {
        setCompleted(true);
        router.push(`/quiz/results/${instanceId}?celebrate=1`);
        return true;
      }
    } catch {
      // Can't confirm either way — treat like a confirmed 404 below rather
      // than guess and risk stranding the user on an empty results page.
    }
    setQuizError(INSTANCE_UNRECOVERABLE_ERROR, "session_dead");
    return false;
  }

  // Recreate the anonymous session and a fresh questionnaire after the server
  // rejected the old ones (dead session / purged cookie on a resumed tab).
  // Returns the new questionnaire id, or null when recovery itself failed.
  // All quiz answers live in React state, so callers replay completed steps
  // onto the new instance and retry — the user never loses anything.
  async function recoverSession(): Promise<number | null> {
    try {
      const turnstileToken = getToken ? await getToken().catch(() => null) : "";
      // Token acquisition failed while a site key is configured — the session
      // POST would be rejected by the proxy's bot check; don't bother.
      if (turnstileToken === null) return null;

      const sessionRes = await clientFetch(proxyPath("v1/sessions/anonymous"), {
        method: "POST",
        body: JSON.stringify({}),
        ...(turnstileToken ? { headers: { "cf-turnstile-token": turnstileToken } } : {}),
      });
      if (!sessionRes.res.ok) return null;
      // Token consumed — reset so the next gated call gets a fresh one.
      resetToken?.();

      // Fresh request_id: createRequestId was already consumed when the old
      // questionnaire was created under the old session.
      const questionnaireRes = await clientFetch(proxyPath("v1/questionnaires"), {
        method: "POST",
        body: JSON.stringify({ request_id: crypto.randomUUID() }),
      });
      if (!questionnaireRes.res.ok) return null;
      const id = getQuestionnaireId(questionnaireRes.body);
      if (!id) return null;

      setQuestionnaireId(id);
      return id;
    } catch (e) {
      posthog.captureException(e);
      return null;
    }
  }

  // Resolves with the questionnaire id the PATCH actually succeeded against —
  // the recovered instance's id when a dead session forced recovery mid-call,
  // otherwise the entry id — so multi-request flows (onSubmitSkipReview) can
  // thread it into the next call instead of reading this render's stale
  // questionnaireId closure. Falsy (false) on failure.
  async function submitStep(
    stepKey: StepKey,
    payload: unknown,
  ): Promise<number | false> {
    if (!questionnaireId) {
      setQuizError("No questionnaire instance yet. Please retry.", "no_instance");
      return false;
    }

    setBusyOperation(stepKey);
    clearErrors();
    try {
      let activeId = questionnaireId;
      let { res, body } = await patchStep(activeId, stepKey, payload);

      // Dead session / questionnaire (stale resumed tab): recreate both and
      // replay from in-memory state, then retry this step once. Single attempt
      // by construction — the retry below is never re-entered.
      if (!res.ok && isDeadSessionOrQuestionnaireError(res.status, body)) {
        const newId = await recoverSession();
        if (newId === null) {
          setQuizError(SESSION_RECOVERY_FAILED_ERROR, "session_dead");
          return false;
        }
        if (stepKey === "constraints") {
          // Re-establish step 1 on the new instance before retrying step 2.
          const replay = await patchStep(newId, "weight_priorities", buildPrioritiesPayload());
          if (!replay.res.ok) return applyApiError(replay.res.status, replay.body);
        }
        activeId = newId;
        ({ res, body } = await patchStep(newId, stepKey, payload));
      }

      if (!res.ok) {
        // save_step (Django) raises this same code for ANY
        // non-IN_PROGRESS status, not only completion — most reachably an
        // ABANDONED instance from a mid-quiz login conflict — so it needs
        // the verify-first recovery, not the flat "validation, no button"
        // applyApiError would otherwise give it.
        if (normalizeDrfError(body, res.status).code === "questionnaire_not_in_progress") {
          await tryRecoverNotInProgress(activeId);
          return false;
        }
        return applyApiError(res.status, body);
      }

      // A throw inside capture must never look like the PATCH itself failed
      // (it would land in this function's own catch below and refuse to
      // advance the step even though the server already committed it) —
      // hence safeCapture, not a direct posthog.capture call.
      //
      // Counting note: fires once per successful PATCH — going back then
      // forward through a step re-fires it, so this is an engagement signal,
      // not an exactly-once-per-session counter. It also does NOT fire for
      // the step-1 replay above (stepKey === "constraints" branch) or for
      // either step's replay inside submitComplete()'s own dead-session
      // recovery — both call patchStep() directly rather than going through
      // submitStep(), by construction (the replay only needs to reach
      // Django, not re-count as a fresh step completion).
      safeCapture("quiz_step_completed", { step: stepKey, questionnaire_id: activeId });
      return activeId;
    } catch (e) {
      // This used to also swallow AbortError on the theory
      // that a user-navigated-away abort has nobody left to show a card to —
      // but nothing in this file ever passes an AbortSignal into patchStep,
      // so the only real producer of AbortError here is the BROWSER
      // discarding an in-flight fetch (iOS backgrounding the tab — the same
      // class of problem usePageResume exists for elsewhere in this file).
      // The user comes back to that case, so it needs the same card as any
      // other network failure, not silence.
      posthog.captureException(e);
      setQuizError(SUBMIT_NETWORK_ERROR, "retryable");
      return false;
    } finally {
      setBusyOperation(null);
    }
  }

  // `idOverride` lets a caller that just recovered the session (via
  // submitStep's return value) target the recovered questionnaire directly —
  // setQuestionnaireId is async, so this render's questionnaireId closure
  // still points at the old, dead instance and would force a second,
  // redundant recovery here.
  async function submitComplete(idOverride?: number) {
    const startId = idOverride ?? questionnaireId;
    if (!startId) {
      setQuizError("No questionnaire instance yet. Please retry.", "no_instance");
      return false;
    }

    setBusyOperation("complete");
    clearErrors();
    try {
      // Scoped by instance id, with a constant payload hash — stable for the
      // instance's entire lifetime (see requestIdFor above). That's exactly
      // the lost-response protection this needs: a retry against the SAME
      // instance always replays the same request_id, whether it's the silent
      // auto-retry below, transparent session recovery re-targeting a new
      // instance (a fresh id via the new instance's own scope), or the user
      // pressing "Try again" on the error card.
      const postComplete = (id: number) =>
        withDroppedConnectionRetry(
          () =>
            clientFetch(proxyPath(`v1/questionnaires/${id}/complete`), {
              method: "POST",
              body: JSON.stringify({
                request_id: requestIdFor(`${id}:complete`, { confirm: true }),
                confirm: true,
              }),
            }),
          "complete",
        );

      let resultsId = startId;
      let { res, body } = await postComplete(startId);

      // Dead session / questionnaire: recreate, replay both steps from
      // in-memory state, and retry the complete once (see submitStep).
      if (!res.ok && isDeadSessionOrQuestionnaireError(res.status, body)) {
        const newId = await recoverSession();
        if (newId === null) {
          setQuizError(SESSION_RECOVERY_FAILED_ERROR, "session_dead");
          return false;
        }
        const prioritiesReplay = await patchStep(newId, "weight_priorities", buildPrioritiesPayload());
        if (!prioritiesReplay.res.ok) return applyApiError(prioritiesReplay.res.status, prioritiesReplay.body);
        const constraintsReplay = await patchStep(newId, "constraints", buildConstraintsPayload());
        if (!constraintsReplay.res.ok) return applyApiError(constraintsReplay.res.status, constraintsReplay.body);
        resultsId = newId;
        ({ res, body } = await postComplete(newId));
      }

      if (!res.ok) {
        const norm = normalizeDrfError(body, res.status);

        // The stable request_id already fixes the
        // lost-response-against-a-COMPLETED-instance case on its own (Django
        // replays 200 when the request_id matches — see
        // tryRecoverNotInProgress's comment), which means this branch's real
        // population in production is mostly the OTHER case that code
        // covers: an ABANDONED instance with no results to send anyone to.
        // Deliberately no quiz_completed on the navigate-to-results path
        // inside tryRecoverNotInProgress — this run's own completion already
        // fired it (or another tab's did), and there's no way to tell which
        // without risking a duplicate count.
        if (norm.code === "questionnaire_not_in_progress") {
          return tryRecoverNotInProgress(resultsId);
        }

        setQuizError(
          humanizeApiMessage(norm.message, res.status, norm.code),
          isRetryableStatus(res.status) ? "retryable" : "validation",
        );
        setFieldErrors(norm.fieldErrors);
        setMissingRequired(norm.missingRequiredFields);
        if (norm.missingRequiredFields) {
          const missing = norm.missingRequiredFields;
          if (missing.weight_priorities?.length) setStep(1);
          else if (missing.constraints?.length) setStep(2);
        }
        return false;
      }

      setCompleted(true);
      // Single success exit for submitComplete(), including after the dead-session
      // recover-and-retry above — fires exactly once per completion.
      trackMetaEvent(META_PIXEL_EVENT.quizComplete);
      // safeCapture, not a direct posthog.capture call — this sits inside the
      // same try as the postComplete() call above and fires before the
      // redirect below, so a throw here must never be able to land in this
      // function's own catch and misreport a server-committed completion as
      // a network failure (see safeCapture's module doc).
      safeCapture("quiz_completed", {
        budget_flexibility: constraints.budgetFlexibility,
        questionnaire_id: resultsId,
      });
      // `celebrate=1` signals the results page to hold its branded loading
      // splash for a beat on this first arrival only — reloads, back-nav, and
      // later refreshes should render as fast as the data allows.
      router.push(`/quiz/results/${resultsId}?celebrate=1`);
      return true;
    } catch (e) {
      // See submitStep's matching catch — AbortError here means the browser
      // discarded the fetch (iOS backgrounding the tab), not a user-driven
      // navigation with nobody left to see the card, so it gets the same
      // card as any other network failure.
      posthog.captureException(e);
      setQuizError(SUBMIT_NETWORK_ERROR, "retryable");
      return false;
    } finally {
      setBusyOperation(null);
    }
  }

  const inputsLocked = initState !== "ready" || Boolean(busyOperation) || completed;
  const canGoBack = step > 1 && !completed;

  const canContinue = useMemo(() => {
    if (inputsLocked) return false;
    if (step === 1) return prioritiesSchema.safeParse(priorities).success;
    if (step === 2) {
      const missing = missingConstraintFields(constraints, trip);
      return !missing.budget && !missing.children && !missing.disabilities && !missing.disabilityType;
    }
    return true;
  }, [constraints, inputsLocked, priorities, step, trip]);

  async function patchConstraints(): Promise<number | false> {
    return submitStep("constraints", buildConstraintsPayload());
  }

  async function onNext() {
    lastSubmitRef.current = "onNext";
    if (!canContinue) return;
    if (step === 1) {
      const ok = await submitStep("weight_priorities", buildPrioritiesPayload());
      if (ok) setStep(2);
      return;
    }

    if (step === 2) {
      const ok = await patchConstraints();
      if (ok) setStep(3);
    }
  }

  async function onSubmitSkipReview() {
    lastSubmitRef.current = "onSubmitSkipReview";
    if (!canContinue) return;
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    // Paint the splash before the awaited POSTs below, not after — otherwise
    // the constraints form stays on screen for the full round-trip(s).
    setSubmitting(true);
    let navigated = false;
    try {
      const patchedId = await patchConstraints();
      if (!patchedId) return;
      // Pass the id the PATCH actually succeeded against: if it recovered the
      // session, the closure's questionnaireId is stale and completing against
      // it would trigger a second, redundant recovery.
      navigated = await submitComplete(patchedId);
      // On success submitComplete() has already called router.push(); leave
      // the splash up so there's no flash of the form before the route takes
      // over. On failure, fall through to clear it in `finally`.
    } finally {
      submitInFlightRef.current = false;
      if (!navigated) setSubmitting(false);
    }
  }

  const onChangeTrip = useCallback(
    (next: TripProfileDraft) => {
      if (inputsLocked) return;
      setTrip(next);
    },
    [inputsLocked],
  );

  async function onSubmit() {
    lastSubmitRef.current = "onSubmit";
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    setSubmitting(true);
    let navigated = false;
    try {
      navigated = await submitComplete();
    } finally {
      submitInFlightRef.current = false;
      if (!navigated) setSubmitting(false);
    }
  }

  // A step-3 /complete failure's error card was surviving
  // navigation to a different step — e.g. "Edit answers" back to step 2,
  // where the stale card's "Try again" then silently re-ran /complete
  // against the PRE-EDIT constraints instead of saving what the user just
  // changed. Clearing the error (and lastSubmitRef, so a leftover
  // retryLastSubmit call can't fire a dispatcher for a step the user has
  // left) on every navigation away from the step that raised it closes that.
  const onBack = canGoBack
    ? () => {
        clearErrors();
        lastSubmitRef.current = null;
        setStep((s) => Math.max(1, s - 1));
      }
    : undefined;
  const onEdit = (target: 1 | 2) => {
    clearErrors();
    lastSubmitRef.current = null;
    setStep(target);
  };

  async function restartQuiz() {
    resetToken?.();
    initStartedRef.current = false;
    await init();
  }

  // Re-dispatches whichever submit last failed. Resolved through a tag
  // (lastSubmitRef), not a captured function, so this always calls the
  // dispatcher's current closure — current step, current canContinue, current
  // form state — rather than replaying the exact payload that failed. A user
  // who edited a field after the error therefore submits what's on screen.
  async function retryLastSubmit() {
    switch (lastSubmitRef.current) {
      case "onNext":
        return onNext();
      case "onSubmitSkipReview":
        return onSubmitSkipReview();
      case "onSubmit":
        return onSubmit();
      case null:
        return;
    }
  }

  return {
    initState,
    step,
    stepImageOrders,
    trip,
    priorities,
    constraints,
    geoOptions,
    geoError,
    priorityDescriptions,
    busyOperation,
    submitting,
    error,
    errorKind,
    errorNonce,
    fieldErrors,
    missingRequired,
    completed,
    canContinue,
    inputsLocked,
    onNext,
    onSubmitSkipReview,
    onChangeTrip,
    onBack,
    onSubmit,
    onEdit,
    restartQuiz,
    retryLastSubmit,
    setPriorities,
    setConstraints: handleChangeConstraints,
  };
}
